export default function Help() {
  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <article className="prose prose-slate dark:prose-invert max-w-none
        [&_mark]:bg-orange-100 [&_mark]:text-orange-800 [&_mark]:px-1 [&_mark]:py-0.5 [&_mark]:rounded-sm
        [&_pre]:bg-slate-800 [&_pre]:text-slate-100 [&_pre]:p-4 [&_pre]:rounded-md [&_pre]:my-2
        [&_code]:bg-slate-700 [&_code]:text-orange-300 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
        [&_pre_code]:bg-transparent [&_pre_code]:text-slate-100 [&_pre_code]:p-0
        [&_.lead]:text-xl [&_.lead]:mb-8 [&_.lead]:text-slate-600 [&_.lead]:dark:text-slate-400">
        <header>
          <h1>Glaium Help</h1>
          <p className="lead">
            Learn how to use Glaium's powerful data integration and optimization platform
          </p>
        </header>

        <h2>Systems</h2>
        <p>
          Systems are structures containing data streams Integrators, Normalizations, 
          Primitives (metrics and dimensions) and Optimizations.
        </p>

        <h2>Integrations</h2>
        <p>
          Integrations are the "data pipes" connecting Glaium with external data streams 
          that can be either sources or destinations, therefore they can be respectively 
          pull or push. Glaium has currently more than 300 Integrators with the main 
          partners in the market and can easily exchange data via APIs, OAuth, CSV Files 
          or directly with DBs.
        </p>

        <h2>Cube</h2>
        <p>
          All data collected via Integrations is stored internally as a Data Cube 
          containing the Primitives (Metrics, Timestamp and Dimensions).
        </p>

        <h2>Primitives</h2>
        <p>
          Primitives are the representation of KPIs either collected from external sources 
          via Integrations or calculated on-the-fly with formulas. They are:
        </p>
        
        <ul>
          <li>
            <strong>Metrics</strong> - numeric values (collected from Integrations), constant 
            values or formulas. Ex: <strong>ecpm = ad_revenue / (ad_impressions/1000)</strong> where 
            ecpm is a calculated metric and both ad_revenue and ad_impressions are from data collection.
          </li>
          <li>
            <strong>Dimensions</strong> - categorical text values used to breakdown metrics. 
            (Ex: Country, App, DateTime...)
          </li>
          <li>
            <strong>Global</strong> - global constants used by some functions. (Ex: 
            global.conditional = yyyymmdd not in ('2024-06-02') ... to exclude June 2nd data 
            from any report in the system)
          </li>
        </ul>

        <p>
          There is no need to describe Metrics and Dimensions coming from data collection 
          as they are automatically added to the Cube with their original names. For the 
          calculated metrics thought it is important to follow the syntax below:
        </p>

        <pre><code>primitive_name = math_formula or numeric_value</code></pre>

        <p>Dimension names can also be changed easily using:</p>

        <pre><code>new_dimension_name = current_dimension_name</code></pre>

        <h2>Normalizations</h2>
        <p>
          Normalizations allow you to standardize, transform, and replace values for Metrics and
          Dimensions. Each KPI can have multiple transformation rules defined in YAML format,
          applied in sequence.
        </p>

        <h3>YAML Structure</h3>
        <p>
          Normalization rules are defined in YAML. Each key is the rule type:
        </p>

        <pre><code>{`rule_type:
  param: value`}</code></pre>

        <p>Multiple rules can be combined:</p>

        <pre><code>{`upper: true
replace:
  old_value: new_value`}</code></pre>

        <h3>Rule Types</h3>

        <ul>
          <li>
            <strong>Replace</strong> - Find and replace exact values. Keys are search terms, values are replacements.
            <pre><code>{`replace:
  old_value: new_value
  "": N/A`}</code></pre>
          </li>
          <li>
            <strong>Partial String</strong> - Substring replacement with regex patterns.
            <pre><code>{`partial_string:
  "ABC.*": ABC Media
  "XYZ.*": XYZ Network`}</code></pre>
          </li>
          <li>
            <strong>Case Transform</strong> - Change text case. Use <code>upper</code>, <code>lower</code>, or <code>title</code>.
            <pre><code>{`upper: true`}</code></pre>
            <pre><code>{`lower: true`}</code></pre>
            <pre><code>{`title: true`}</code></pre>
          </li>
          <li>
            <strong>Countries</strong> - Standardize country names to official formats.
            <pre><code>{`countries:
  output: "ISO 2"`}</code></pre>
            <p>Formats: <code>ISO 2</code> (US), <code>ISO 3</code> (USA), <code>name</code> (United States)</p>
          </li>
          <li>
            <strong>Currency</strong> - Convert monetary values using daily exchange rates.
            <pre><code>{`currency: EUR`}</code></pre>
          </li>
          <li>
            <strong>Fuzzy Matching</strong> - Match similar values to a predefined list using fuzzy string matching.
            <pre><code>{`fuzzy:
  - Pong
  - Tetris
  - Solitaire
  - others`}</code></pre>
            <p>Include <code>others</code> at the end to group unmatched values as "Others".</p>
          </li>
          <li id="conditionals">
            <strong>Conditional</strong> - Rule-based value assignment with conditions. Keys are pandas-style conditions, values are the result if condition matches.
            <pre><code>{`conditional:
  "channel in ['Direct', 'Distribution']": Direct
  "channel in ['Open Auction', 'Non-Display']": Programmatic
  default: Internal`}</code></pre>
            <p>The <code>default</code> key specifies the value when no conditions match.</p>
          </li>
        </ul>

        <h3>Anonymize</h3>
        <p>
          Each KPI can be marked as <strong>Anonymize</strong> using the toggle in the table.
          When enabled, this KPI's values will be anonymized when sending data to AI Agents,
          replacing sensitive values with hashed identifiers (e.g., <code>app§§963D9D63B7701FC5</code>).
          The original values can be recovered using the de-anonymization API.
        </p>

        <h2>Optimizations</h2>
        <p>
          Optimizations are specific analysis and adhoc data cuts that can be done with 
          Primitives within each of the Systems.
        </p>

        <h3>Reports</h3>
        <p>
          Reports allow users to create adhoc data cuts. Glaium offers two modes for creating reports:
        </p>

        <h4>Smart Mode (Natural Language)</h4>
        <p>
          The Smart Mode allows you to type natural language queries to build reports quickly.
          Simply type what you want to see and press Enter to confirm your selections.
        </p>

        <p><strong>Basic Syntax:</strong></p>
        <pre><code>installs by country last 7 days</code></pre>
        <pre><code>revenue spend cpi by app platform pivot country this month</code></pre>
        <pre><code>ua_spent by campaign_name if integrator = google_ads last 30 days</code></pre>

        <p><strong>Components:</strong></p>
        <ul>
          <li>
            <strong>Metrics</strong> - Type metric names (e.g., <code>installs</code>, <code>revenue</code>, <code>spend</code>).
            The system will fuzzy-match your input to available metrics.
          </li>
          <li>
            <strong>Dimensions</strong> - Use <code>by</code> followed by dimension names (e.g., <code>by country app</code>).
            Without "by", the system intelligently categorizes words as metrics or dimensions.
          </li>
          <li>
            <strong>Pivot</strong> - Use <code>pivot</code> to create column headers (e.g., <code>pivot platform</code>).
          </li>
          <li>
            <strong>Date Ranges</strong> - Natural language dates are supported:
            <ul>
              <li><code>last 7 days</code>, <code>last 30 days</code>, <code>last 3 months</code></li>
              <li><code>this week</code>, <code>this month</code>, <code>this quarter</code>, <code>this year</code></li>
              <li><code>last week</code>, <code>last month</code>, <code>last quarter</code>, <code>last year</code></li>
              <li><code>yesterday</code>, <code>today</code></li>
              <li>Relative format: <code>d-10 d0</code> (from 10 days ago to today)</li>
            </ul>
          </li>
          <li>
            <strong>Conditionals</strong> - Use <code>if</code>, <code>where</code>, or <code>conditional</code> followed by filter conditions:
            <ul>
              <li><code>if platform = Android</code></li>
              <li><code>where country in (US, CA, UK)</code></li>
              <li><code>if spend &gt; 1000 and installs &gt; 100</code></li>
            </ul>
          </li>
        </ul>

        <p><strong>Tips:</strong></p>
        <ul>
          <li>Press <strong>Enter</strong> to confirm your input and convert it to badges</li>
          <li>Drag and drop badges to reorder metrics, dimensions, or pivot columns</li>
          <li>Click the X on any badge to remove it</li>
          <li>The system shows hints as you type to help you find matching metrics and dimensions</li>
          <li>String values in conditionals are automatically quoted when needed</li>
        </ul>

        <h4>Advanced Mode (Selectors)</h4>
        <p>
          For more precise control, use Advanced Mode with dropdown selectors for each field.
          Toggle between Smart and Advanced modes using the buttons at the top of the filter panel.
        </p>

        <p><strong>Available Fields:</strong></p>
        <ul>
          <li>
            <strong>Metrics</strong> - Multi-select dropdown to choose one or more metrics
            (similar to <em>SELECT</em> in SQL). Required field.
          </li>
          <li>
            <strong>Dimensions</strong> - Multi-select dropdown to group data by one or more
            dimensions (similar to <em>GROUP BY</em> in SQL).
          </li>
          <li>
            <strong>Pivot</strong> - Multi-select dropdown to pivot dimensions as column headers.
            Creates a cross-tabulation view of your data. Optional.
          </li>
          <li>
            <strong>Conditionals</strong> - Text input for filtering data (similar to <em>WHERE</em> in SQL).
            Optional. See syntax details below.
          </li>
          <li>
            <strong>Start Date / End Date</strong> - Date inputs supporting:
            <ul>
              <li>Relative dates: <code>d-7</code> (7 days ago), <code>d0</code> (today)</li>
              <li>Natural language: <code>yesterday</code>, <code>last week</code></li>
              <li>Absolute dates: <code>2024-01-15</code></li>
            </ul>
          </li>
          <li>
            <strong>Frequency</strong> - How often to run the report: Once, Hourly, Daily, or Weekly.
          </li>
          <li>
            <strong>Push Integration</strong> - Optional destination to automatically send the report
            (e.g., Google Sheets, Slack).
          </li>
        </ul>

        <h4>Conditional Syntax</h4>
        <p>
          Conditionals filter data before loading from the Cube using SQL-like syntax.
        </p>

        <p><strong>Basic Rules:</strong></p>
        <ul>
          <li><strong>String Comparisons:</strong> Use single quotes: <code>platform = 'Android'</code></li>
          <li><strong>Operators:</strong> <code>=</code>, <code>!=</code>, <code>&lt;</code>, <code>&lt;=</code>, <code>&gt;</code>, <code>&gt;=</code></li>
          <li><strong>Case Sensitive:</strong> <code>'android'</code> ≠ <code>'Android'</code></li>
          <li><strong>Numeric Values:</strong> No quotes needed: <code>revenue &gt; 1000</code></li>
        </ul>

        <p><strong>Combining Conditions:</strong></p>
        <ul>
          <li><strong>AND:</strong> <code>platform = 'Android' AND country = 'US'</code></li>
          <li><strong>OR:</strong> <code>platform = 'Android' OR platform = 'iOS'</code></li>
          <li><strong>Parentheses:</strong> <code>(platform = 'Android' OR platform = 'iOS') AND revenue &gt; 1000</code></li>
        </ul>

        <p><strong>IN Clause:</strong></p>
        <ul>
          <li><code>platform IN ('Android', 'iOS')</code></li>
          <li><code>country NOT IN ('Bot Traffic', 'Unknown')</code></li>
        </ul>

        <p><strong>Pattern Matching:</strong></p>
        <ul>
          <li><code>campaign LIKE 'Holiday%'</code> - starts with "Holiday"</li>
          <li><code>app LIKE '%Game%'</code> - contains "Game"</li>
          <li><code>UPPER(campaign) LIKE 'HOLIDAY%'</code> - case-insensitive</li>
        </ul>

      </article>
    </div>
  )
}